<template>
  <div class="modal" v-if="isActive" @click="closeOnClickOutside">
    <div class="modal__content flex flex-col">
      <div class="modal__row modal__header flex-0">
        <h2 class="title modal__title">{{ props?.title || t('modals.defaultTitle') }}</h2>
        <div data-testid="modal-close" v-if="props.buttons?.close !== false" class="modal__close" @click="() => { isActive = false; emits('cancel') }">
          <svg class="icon icon_size_xs stroke-current">
            <use xlink:href="/img/sprites/icons-sprite.svg#icon-close"></use>
          </svg>
        </div>
      </div>
      <div class="modal__notification flex-1 overflow-y-auto">
        <slot/>
      </div>
      <div class="modal__footer flex-0">
        <btn-wrapper
            v-if="props.buttons?.primary !== false"
            data-testid="modal-finish-button"
            @click="isActive = false; emitEvent('finish')"
            :disabled="isTimerActive || lock"
            :params="{
                        class: 'modal__send-btn modal__btn !bg-default min-w-[120px] justify-center',
                        type: 'button',
                        title: (isTimerActive && props.enableTimer) ? timeLeft : props.buttons.primary?.text || t('modals.primary'),
                    }"
        />
        <btn-wrapper
            v-if="props.buttons?.cancel !== false"
            @click="() => { isActive = false; emitEvent('cancel') }"
            :params="{
                        class: 'modal__btn modal__cancel-btn min-w-[120px] justify-center',
                        type: 'button',
                        title: props?.text?.cancel || t('modals.cancel'),
                    }"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, onUnmounted, defineEmits, defineProps, computed, watch} from 'vue'
import {useI18n} from 'vue-i18n'
import constant from '@/consts/const.json'

const isTimerActive = ref(false)
const timeLeft = ref(constant.timer.EARLY_EXIT_DELAY_IN_SECONDS)
const emits = defineEmits(['cancel', 'finish', 'update:modelValue'])
const {t} = useI18n()
const props = defineProps({
    modelValue: Boolean,
    text: {
        type: Object
    },
    buttons: {
        type: Object,
        default: () => ({
            cancel: true,
            primary: true,
            close: true
        })
    },
    title: {
        type: String
    },
    enableTimer: {
        type: Boolean,
        default: false
    },
    lock: {
        type: Boolean,
        default: false
    }
})

let intervalId

const startTimer = () => {
    if (timeLeft.value) {
        isTimerActive.value = true

        intervalId = setInterval(() => {
            timeLeft.value -= 1

            if (timeLeft.value <= 0) {
                clearInterval(intervalId)

                isTimerActive.value = false
            }
        }, 1000)
    }
}

const isActive = computed({
    get() {
        return props.modelValue
    },
    set(value) {
        emits('update:modelValue', value)
    }
})

const EscapeEvent = (e) => {
    if (e.key === 'Escape') {
        isActive.value = false
    }
}

const closeOnClickOutside = ({target}) => {
    if (!target.closest('.modal__content') && target.classList[1] !== 'confirmation__delete') {
        isActive.value = false
        emits('cancel')
    }
}

onMounted(() => {
    document.addEventListener('keydown', EscapeEvent)
})

onUnmounted(() => {
    document.removeEventListener('keydown', EscapeEvent)

    clearInterval(intervalId)
})

watch(() => isActive.value, () => {
    timeLeft.value = constant.timer.EARLY_EXIT_DELAY_IN_SECONDS

    if (isActive.value && props.enableTimer) {
        startTimer()
    } else {
        clearInterval(intervalId)
    }
})

const emitEvent = _type => {
  emits(_type)
}
</script>

<style lang="scss" scoped>
@import './scss/modal.scss';
</style>
